import { useCallback, useEffect, useMemo } from "react"
import { useDispatch } from "react-redux"
import moment from "moment"
import { getToday, Period, TIME_FORMAT_TZ } from "helpers/dates"
import { DataStatus, LOADING } from "helpers/data-statuses"
import { downloadXlsx } from "helpers/csv"
import { Creators as DialogCreators } from "modules/dialog/model"
import { IsoDate } from "helpers/types/dates"
import { DownloadSheet } from "hooks/register-downloads"

// [TODO:TS] Find better location
interface IntradayMetadata {
  id: string
  asOf: string
}

/**
 * Custom hook to handle intraday data refresh workflow
 * Automatically refreshes data when appropriate, or shows manual refresh button
 */
export function useIntradayRefresh(
  isIntraday: boolean,
  intradayDataAvailable: boolean,
  useLatestDate: boolean,
  lastIntradayBatch: IntradayMetadata,
  intradayBatchMetadata: IntradayMetadata,
  setIntradayBatchMetadata: (metadata: IntradayMetadata) => void,
  setPeriod: (period: Period | { period: Period, startDate: IsoDate, endDate: IsoDate }) => void,
  currentPeriod: Period,
  adjustedStartDate: IsoDate,
  // [TODO:TS] Ideally type when trades are typed, but we only care about the length
  trades: unknown[],
  resetTrades: () => void,
  onDownloadData: () => { fileName: string, data: { sheets: DownloadSheet[] } | null },
  isPreferencesLoaded: boolean,
  isEndDateIntraday: boolean,
  lastIntradayBatchStatus: DataStatus,
  setUseLatestDate: (useLatestDate: boolean) => void,
) {
  const dispatch = useDispatch()
  const intradayDate = getToday()

  const newDataAvailable = isIntraday && lastIntradayBatch?.id !== intradayBatchMetadata?.id

  const shouldAutoRefresh = useMemo(() => {
    return newDataAvailable && useLatestDate && intradayDataAvailable && trades?.length === 0 && isPreferencesLoaded
  }, [newDataAvailable, useLatestDate, intradayDataAvailable, trades, isPreferencesLoaded])

  const refreshIntradayData = useCallback(() => {
    setIntradayBatchMetadata({
      asOf: lastIntradayBatch?.asOf,
      id: lastIntradayBatch?.id,
    })
    setUseLatestDate(true)
    setPeriod({
      period: currentPeriod,
      startDate: adjustedStartDate,
      endDate: intradayDate,
    })
  }, [setIntradayBatchMetadata, lastIntradayBatch?.asOf, lastIntradayBatch?.id, setUseLatestDate, currentPeriod, setPeriod, adjustedStartDate, intradayDate])

  const onRefreshPositions = useCallback(() => {
    resetTrades()
    setIntradayBatchMetadata({
      asOf: lastIntradayBatch.asOf,
      id: lastIntradayBatch.id,
    })
    setPeriod({
      period: currentPeriod,
      startDate: adjustedStartDate,
      endDate: intradayDate,
    })
  }, [resetTrades, setIntradayBatchMetadata, lastIntradayBatch, adjustedStartDate, intradayDate, currentPeriod, setPeriod])

  // Show dialog when user has trades and wants to refresh
  const showDialog = useCallback((onConfirm: () => void, onDownload: () => { fileName: string, data: { sheets: DownloadSheet[] } | null }, newAsOf: string) => {
    dispatch(DialogCreators.open({
      title: "Confirm Refresh & Clear All Trades",
      contentText: `There is new intraday data available as of ${newAsOf}. Refreshing will reset your trades. Would you still like to proceed?`,
      buttons: [{
        text: "Download & Refresh",
        type: "secondary",
        action: async () => {
          const { data, fileName } = onDownload()
          await downloadXlsx(fileName, data, {
            type: "intraday-refresh-download",
            location: "dashboard",
          })
          onConfirm()
        },
      }, {
        text: "Refresh",
        type: "primary",
        action: onConfirm,
      }],
    }))
  }, [dispatch])

  const handleIntradayRefresh = useCallback(() => {
    if (trades?.length > 0) {
      const newAsOf = moment(lastIntradayBatch?.asOf, TIME_FORMAT_TZ).format("LTS")
      showDialog(onRefreshPositions, onDownloadData, newAsOf)
    } else {
      refreshIntradayData()
    }
  }, [trades, showDialog, onRefreshPositions, onDownloadData, lastIntradayBatch, refreshIntradayData])

  useEffect(() => {
    if (shouldAutoRefresh) {
      refreshIntradayData()
    }
  }, [shouldAutoRefresh, refreshIntradayData])

  // Determine if we should show the manual refresh button
  const showRefreshButton = useMemo(() => {
    // Show refresh button if:
    // 1. New data is available and won't auto-refresh, OR
    // 2. Viewing historical dates with intraday data available
    const newDataNeedsRefresh = newDataAvailable && !shouldAutoRefresh
    const historicalWithIntradayData = intradayDataAvailable && !isEndDateIntraday
    return newDataNeedsRefresh || historicalWithIntradayData
  }, [newDataAvailable, shouldAutoRefresh, intradayDataAvailable, isEndDateIntraday])

  const refreshButtonTooltip = useMemo(() => {
    const newDataNeedsRefresh = newDataAvailable && !shouldAutoRefresh
    if (newDataNeedsRefresh) {
      return "New intraday data available"
    }
    return "Load intraday data as end date"
  }, [newDataAvailable, shouldAutoRefresh])

  // Determine if we should show the warning icon
  const showWarning = useMemo(() => {
    return isIntraday && !intradayDataAvailable && lastIntradayBatchStatus !== LOADING
  }, [isIntraday, intradayDataAvailable, lastIntradayBatchStatus])

  return {
    newDataAvailable,
    showRefreshButton,
    showWarning,
    handleIntradayRefresh,
    refreshIntradayData,
    refreshButtonTooltip,
  }
}
